import { Stack } from '@mantine/core';
import { EmptyState } from '@repo/common/components/NewEmptyState/EmptyState';
import { EntityType } from '@repo/common/enums/entityType';
import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import type { ISecodaEntity } from '../../api';
import { useAuthUser } from '../../api';
import {
	fetchCatalogList,
	useCatalogInfiniteList,
} from '../../api/hooks/resourceCatalog';
import { useTeamEmptyState } from '../../api/hooks/team/emptyStates';
import { entityModalStore } from '../../components/EntityModal/store';
import { TableV2 } from '../../components/TableV2';
import type { OnCellClickHandlerParams } from '../../components/TableV2/types';
import { useEntityBreadcrumbTeam } from '../../hooks/useEntityBreadcrumbs';
import { useHomepageTeam } from '../HomePage/hooks/useHomepageTeam';
import { useActions, useColumns } from './TeamBIDashboardsPage.hooks';

const COLUMN_VISIBILITY = {
	catalogType: EntityType.bi_dashboard,
	catalogServerType: EntityType.bi_dashboard,
} as const;

export default function TeamBIDashboardsPage() {
	const { teamId } = useParams();

	const navigate = useNavigate();
	const { user, isLoading: isUserLoading, isAdminUser } = useAuthUser();
	const { isTeamEditorUser } = useHomepageTeam();

	const columns = useColumns();
	const actions = useActions();

	const teamEmptyState = useTeamEmptyState(teamId as string, 'bi_dashboard');
	const { setTeamBreadcrumb } = useEntityBreadcrumbTeam();

	useEffect(() => {
		// If the user opens a entity page from this context we want the team
		// breadcrumb to match the team of this page.
		if (teamId) {
			setTeamBreadcrumb(teamId);
		}
	}, [setTeamBreadcrumb, teamId]);

	const handleCellClick = useCallback(
		({ column, record }: OnCellClickHandlerParams<ISecodaEntity>) => {
			if (column.navigate !== false) {
				navigate(`/bi/dashboards/${record.search_metadata?.bi_dashboard_id}`);
			}
		},
		[navigate]
	);

	const handleCreateDashboardClick = useCallback(async () => {
		if (!teamId || isUserLoading || !user.id) return;

		await entityModalStore.quickCreateEntity(
			EntityType.bi_dashboard,
			'',
			[teamId],
			{ owners: [user.id] },
			navigate
		);
	}, [teamId, user.id, isUserLoading, navigate]);

	const defaultSearchParams = useMemo(
		() => ({
			team_id: teamId,
			entity_type: EntityType.bi_dashboard,
		}),
		[teamId]
	);

	const handleLearnMore = () => {
		window.open('https://docs.secoda.co/features/dashboards', '_blank');
	};

	if (teamEmptyState?.is_empty) {
		return (
			<EmptyState
				heading="Track company metrics in one place"
				body="Dashboards bring key metrics together in one view to help you track performance, monitor trends, and explore your data."
				animatedIllustrationName="dashboard"
				buttons={[
					{
						label: 'Learn more',
						variant: 'default',
						onClick: handleLearnMore,
					},
					{
						label: 'Create dashboard',
						variant: 'primary',
						onClick: handleCreateDashboardClick,
						requiresUseButtonPermission: true,
					},
				]}
				hasUseButtonPermission={isTeamEditorUser || isAdminUser}
			/>
		);
	}

	return (
		<Stack spacing="lg" m="xl" mt="0">
			<TableV2<ISecodaEntity>
				columns={columns}
				withActions={actions}
				pluralTypeString="dashboards"
				columnVisibility={COLUMN_VISIBILITY}
				// @ts-ignore
				usePaginationList={useCatalogInfiniteList}
				fetchPaginationList={fetchCatalogList}
				withSearch
				withCheckbox
				withInteractiveHeader
				defaultRequiredSearchParams={defaultSearchParams}
				onCellClick={handleCellClick}
			/>
		</Stack>
	);
}
